import classes from "./NutritionBars.module.css";
import PieChartWithoutSectors from "../common/PieChartWithoutSectors";
import useTheme from "../../../../common/hooks/useTheme";
import { useSelector } from "react-redux";
import { RootStatePlatform } from "../../store/platform-slice";

type PieData = { label: string; value: number; categoryType: number };

type Props = {
  data: {
    title: string;
    calorieIntakeValue: number;
    dailyActivityValue: number;
    RMRValue: number;
    workoutValue: number;
    pieTitle: string;
    pieData: PieData[];
  };
};

const NutritionBars = ({ data }: Props) => {
  const theme = useTheme();
  const reportData = useSelector(
    (state: RootStatePlatform) =>
      state.platform.dataPlatformServer.programming.nutritionPlan
  );

  const totalBurnCalories =
    data.dailyActivityValue + data.RMRValue + data.workoutValue;

  const totalIntakeCalories = data.calorieIntakeValue;

  const getNormalizedDataForPieChart = (data: PieData[]) => {
    const normalizedData = data.map((el) => {
      return {
        valueKcal: el.categoryType === 3 ? el.value * 9 : el.value * 4,
        ...el,
      };
    });

    const totalKcal = normalizedData.reduce((acc, el) => acc + el.valueKcal, 0);
    const proteinKcal = normalizedData.find(
      (el) => el.categoryType === 1
    )?.valueKcal;

    const carbsKcal = normalizedData.find(
      (el) => el.categoryType === 2
    )?.valueKcal;

    const percentage1 = Math.round(((proteinKcal ?? 0) / totalKcal) * 100);
    const percentage2 = Math.round(((carbsKcal ?? 0) / totalKcal) * 100);
    return normalizedData.map((el) => {
      return {
        label: el.label,
        value: el.valueKcal,
        valueGrams: el.value,
        valueKcal: el.valueKcal,
        categoryType: el.categoryType,
        percentage:
          el.categoryType === 1
            ? percentage1
            : el.categoryType === 2
            ? percentage2
            : 100 - percentage1 - percentage2,
      };
    });
  };

  return (
    <div style={{ flex: 1, backgroundColor: theme.colors_ds.NEUTRAL_BG_MAIN }}>
      <div
        className={classes.title}
        style={{ color: theme.colors_ds.NEUTRAL_TEXT_DEFAULT }}
      >
        {data.title}
      </div>
      <div
        className={classes.container}
        style={{ borderColor: theme.colors_ds.NEUTRAL_BORDER_WEAK }}
      >
        <div className={classes.barsContainer}>
          <div
            className={classes.barContainerTitle}
            style={{ color: theme.colors_ds.NEUTRAL_TEXT_STRONG }}
          >
            {totalBurnCalories > totalIntakeCalories
              ? `${reportData.deficit}: `
              : `${reportData.surplus}: `}
            <span
              className={classes.barContainerValue}
              style={{
                color:
                  totalBurnCalories > totalIntakeCalories
                    ? theme.colors_ds.DANGER_GRAPH_DEFICIT
                    : theme.colors_ds.SUCCESS_ICON_MAIN,
              }}
            >
              {`${Math.abs(totalBurnCalories - totalIntakeCalories)} kcal`}
            </span>
          </div>
          <div
            className={classes.barHeader}
            style={{ color: theme.colors_ds.NEUTRAL_TEXT_STRONG }}
          >
            <div className={classes.barText}>{reportData.caloricIntake}</div>
            <div
              className={classes.barText}
            >{`${data.calorieIntakeValue} kcal`}</div>
          </div>
          <div className={classes.bar}>
            <div
              className={classes.barInner}
              style={{
                width: `${(totalIntakeCalories * 100) / totalIntakeCalories}%`,
                backgroundColor: theme.colorTokens.P[90],
              }}
            />
          </div>
          <div
            className={classes.barHeader}
            style={{ color: theme.colors_ds.NEUTRAL_TEXT_STRONG }}
          >
            <div className={classes.barText}>{reportData.caloricBurn}</div>
            <div className={classes.barText}>{`${totalBurnCalories} kcal`}</div>
          </div>
          <div className={classes.bar}>
            {data.workoutValue > 0 && (
              <div
                className={classes.barInner}
                style={{
                  width: `${
                    ((data.dailyActivityValue +
                      data.RMRValue +
                      data.workoutValue) *
                      100) /
                    totalBurnCalories
                  }%`,
                  backgroundColor: theme.colorTokens.P[20],
                }}
              />
            )}
            <div
              className={classes.barInner}
              style={{
                width: `${
                  ((data.dailyActivityValue + data.RMRValue) * 100) /
                  totalBurnCalories
                }%`,
                backgroundColor: theme.colorTokens.P[40],
              }}
            />
            <div
              className={classes.barInner}
              style={{
                width: `${
                  (data.dailyActivityValue * 100) / totalBurnCalories
                }%`,
                backgroundColor: theme.colorTokens.P[70],
              }}
            />
          </div>
          <div
            className={classes.legend}
            style={{ color: theme.colors_ds.NEUTRAL_TEXT_STRONG }}
          >
            <div className={classes.legendItem}>
              <div
                className={classes.dataPoint}
                style={{ backgroundColor: theme.colorTokens.P[70] }}
              />
              <div>{reportData.dailyActivity}</div>
            </div>
            <div className={classes.legendItem}>
              <div
                className={classes.dataPoint}
                style={{ backgroundColor: theme.colorTokens.P[40] }}
              />
              <div>{reportData.rmr}</div>
            </div>
            {data.workoutValue > 0 && (
              <div className={classes.legendItem}>
                <div
                  className={classes.dataPoint}
                  style={{ backgroundColor: theme.colorTokens.P[20] }}
                />
                <div>{reportData.workout}</div>
              </div>
            )}
          </div>
        </div>
        <PieChartWithoutSectors
          title={data.pieTitle}
          pieData={getNormalizedDataForPieChart(data.pieData)}
          type={"nutrition"}
        />
      </div>
    </div>
  );
};

export default NutritionBars;
